import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Trophy, Star, Crown, ChevronUp, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SharePanel from "./SharePanel";

const LEVEL_COLORS = {
  Beginner: "from-gray-400 to-gray-500",
  Trader: "from-blue-400 to-blue-600",
  Whale: "from-purple-400 to-purple-600",
  "Market Master": "from-amber-400 to-amber-600",
};

export default function XPSummary({ xpResults, players, winner, onClose, stats }) {
  const { t } = useTranslation();
  const [visibleIdx, setVisibleIdx] = useState(-1);
  const [showLevelUp, setShowLevelUp] = useState(null);

  const winnerEntry = xpResults ? Object.entries(xpResults).find(([k]) => parseInt(k) === winner)?.[1] : null;
  const winnerName = winnerEntry?.name || "Someone";
  const winnerValue = winnerEntry?.crypto_value || 0;
  const meleeCount = stats?.melee_count || 0;

  // Stagger reveal animation
  useEffect(() => {
    if (!xpResults) return;
    const entries = Object.keys(xpResults).sort((a, b) => {
      return (xpResults[a].rank || 99) - (xpResults[b].rank || 99);
    });
    entries.forEach((_, i) => {
      setTimeout(() => setVisibleIdx(i), 600 * (i + 1));
    });
    // Check for level-ups after all revealed
    const totalDelay = 600 * (entries.length + 1);
    setTimeout(() => {
      const leveledUp = entries.find(k => xpResults[k].leveled_up);
      if (leveledUp) setShowLevelUp(xpResults[leveledUp]);
    }, totalDelay);
  }, [xpResults]);

  if (!xpResults) return null;

  const sortedEntries = Object.entries(xpResults).sort(
    ([, a], [, b]) => (a.rank || 99) - (b.rank || 99)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A237E]/90 dark:bg-[#E3F2FD]/90 backdrop-blur-md p-4" data-testid="xp-summary-overlay">
      {/* Level-up animation overlay */}
      {showLevelUp && (
        <div className="absolute inset-0 flex items-center justify-center z-60 pointer-events-none">
          <div className="text-center animate-bounce">
            <ChevronUp className="w-20 h-20 mx-auto text-[#FFC107] drop-shadow-lg" />
            <p className="text-4xl font-bold text-[#FFC107] drop-shadow-lg" style={{ fontFamily: "Fredoka" }}>
              {t("xpSummary.levelUp")}
            </p>
            <p className="text-xl text-white/90">
              {t("xpSummary.levelUpLine", { level: showLevelUp.new_level, name: showLevelUp.level_name })}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-3xl p-6 max-w-lg w-full shadow-2xl border-2 border-white/40 max-h-[90vh] overflow-y-auto"
        data-testid="xp-summary-card">
        {/* Header */}
        <div className="text-center mb-6">
          <Trophy className="w-12 h-12 mx-auto mb-2 text-[#FFC107]" />
          <h2 className="text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
            {t("xpSummary.title")}
          </h2>
        </div>

        {/* Player results */}
        <div className="space-y-3 mb-6">
          {sortedEntries.map(([pidx, result], i) => {
            const isVisible = i <= visibleIdx;
            const isWinner = parseInt(pidx) === winner;
            return (
              <div
                key={pidx}
                className={`rounded-2xl border-2 p-4 transition-all duration-500 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                } ${isWinner ? "border-[#FFC107] bg-[#FFC107]/10" : "border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/50 dark:bg-slate-900/50"}`}
                data-testid={`xp-result-${pidx}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold ${isWinner ? "text-[#FFC107]" : "text-[#1A237E]/40 dark:text-[#E3F2FD]/40"}`}>
                      #{result.rank}
                    </span>
                    {isWinner && <Crown className="w-5 h-5 text-[#FFC107]" />}
                    <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD]">{result.name}</span>
                  </div>
                  <span className="font-bold text-[#38B6FF]">${result.crypto_value}</span>
                </div>

                {/* XP Breakdown */}
                <div className="bg-[#E3F2FD] dark:bg-[#0a1428]/50 rounded-xl p-3 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#1A237E]/60 dark:text-[#E3F2FD]/60">{t("xpSummary.row.participation")}</span>
                    <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("xpSummary.row.xpEarned", { count: result.breakdown.base })}</span>
                  </div>
                  {result.breakdown.win_bonus > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-[#FFC107]">
                        {result.breakdown.win_bonus === 100 ? t("xpSummary.row.winnerBonus") : t("xpSummary.row.secondPlace")}
                      </span>
                      <span className="font-bold text-[#FFC107]">{t("xpSummary.row.xpEarned", { count: result.breakdown.win_bonus })}</span>
                    </div>
                  )}
                  {result.breakdown.missions_completed.map((m) => (
                    <div key={m} className="flex justify-between text-xs">
                      <span className="text-green-600 flex items-center gap-1">
                        <Award className="w-3 h-3" /> {m}
                      </span>
                      <span className="font-bold text-green-600">{t("xpSummary.row.missionXp")}</span>
                    </div>
                  ))}
                  <div className="border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 pt-1.5 flex justify-between text-sm">
                    <span className="font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("xpSummary.row.total")}</span>
                    <span className="font-bold text-[#38B6FF]">{t("xpSummary.row.xpEarned", { count: result.xp_earned })}</span>
                  </div>
                </div>

                {/* Level bar */}
                <div className="mt-2 flex items-center gap-2">
                  <Badge className={`text-[10px] bg-gradient-to-r ${LEVEL_COLORS[result.level_name] || "from-gray-400 to-gray-500"} text-white`}>
                    <Star className="w-3 h-3 mr-0.5" /> {t("xpSummary.level", { level: result.new_level })}
                  </Badge>
                  <span className="text-[10px] text-[#1A237E]/40 dark:text-[#E3F2FD]/40">{t("xpSummary.row.xpTotal", { count: result.total_xp })}</span>
                  {result.leveled_up && (
                    <Badge className="text-[10px] bg-[#FFC107] text-[#1A237E] dark:text-[#E3F2FD] animate-pulse">
                      <ChevronUp className="w-3 h-3 mr-0.5" /> {t("xpSummary.levelUp")}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <Button
          data-testid="xp-summary-close-btn"
          className="w-full h-12 rounded-full bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95"
          onClick={onClose}
        >
          {t("xpSummary.button.continue")}
        </Button>

        {/* ── SHARE VICTORY ── */}
        {winnerEntry && (
          <SharePanel winnerName={winnerName} winnerValue={winnerValue} meleeCount={meleeCount} />
        )}
      </div>
    </div>
  );
}
